"use client";

import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import {
	Anchor,
	ArrowRight,
	BookOpen,
	ChefHat,
	Plane,
	Tv,
	Users,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { IMAGES } from "@/lib/constants/images";

type TimelineItem = {
	year?: string;
	title: string;
	description: React.ReactNode;
	image: string;
	alt: string;
	icon: React.ElementType;
	tags?: string[];
	imageObjectPosition?: string;
	fit?: "cover" | "contain";
	aspectRatio?: string;
	useIntrinsicSize?: boolean;
};

const timelineData: TimelineItem[] = [
	{
		year: "The Foundation",
		title: "A Lifelong Student of Food",
		description: (
			<>
				<p className="mb-4">
					My name is Nick Massie. For as long as I can remember, I&apos;ve been
					obsessed with food. Not just eating it, but understanding it.
				</p>
				<p>
					With over 20 years in professional kitchens (starting at age 14!),
					I&apos;ve dedicated my life to the culinary arts. In 2008, I
					discovered The Paleo Diet, and it changed everything. It wasn&apos;t
					just about food anymore; it was about{" "}
					<strong>fueling a big life.</strong>
				</p>
			</>
		),
		image: IMAGES.about.stackOfBooks,
		alt: "Nick Massie with his culinary book collection",
		icon: BookOpen,
		tags: ["Culinary Arts", "Paleo Diet", "Origins"],
		imageObjectPosition: "object-center",
		aspectRatio: "aspect-[3/4]",
		fit: "cover",
		useIntrinsicSize: true,
	},
	{
		year: "2014-2016",
		title: "Ice Age Meals is Born",
		description: (
			<>
				<p className="mb-4">
					The concept was tested in the fires of competition. I launched{" "}
					<strong>Paleo Nick&apos;s food booth</strong> at the 2014 CrossFit
					Games. The feedback was overwhelming.
				</p>
				<p>
					Athletes needed clean, high-protein convenient food. We realized we
					could ship these meals nationwide. From a small booth to a national
					brand, our mission crystallized:{" "}
					<strong>Debunk nutrition myths and deliver the truth.</strong>
				</p>
			</>
		),
		image: IMAGES.about.paleoNickBooth,
		alt: "Paleo Nick Food Booth at CrossFit Games",
		icon: Users,
		tags: ["CrossFit Games", "Launch", "Community"],
		imageObjectPosition: "object-center",
	},
	{
		year: "Prime Time",
		title: "Into the Shark Tank",
		description: (
			<>
				<p className="mb-4">
					We took our vision to the biggest stage:{" "}
					<strong>ABC&apos;s Shark Tank.</strong>
				</p>
				<p>
					Pitching to hungry sharks like Mark Cuban wasn&apos;t just about
					investment; it was about proving that healthy, frozen food could be
					premium and delicious. Serving our Butternut Squash Lasagna to the
					sharks was a moment of truth—and proof that{" "}
					<strong>our food speaks for itself.</strong>
				</p>
			</>
		),
		image: IMAGES.about.sharkTank.markCuban,
		alt: "Nick Massie pitching to Mark Cuban on Shark Tank",
		icon: Tv,
		tags: ["Shark Tank", "Mark Cuban", "National TV"],
	},
	{
		year: "Global",
		title: "Culinary Adventures",
		description: (
			<>
				<p className="mb-4">
					<em>&quot;He who travels has stories to tell.&quot;</em>
				</p>
				<p>
					We didn&apos;t just stay in the kitchen. I led{" "}
					<strong>Culinary Adventures</strong> around the globe—groups traveling
					to Nicaragua, Thailand, and Alaska. We hiked glaciers, rode elephants,
					and cooked local ingredients. Food is the universal language of
					adventure.
				</p>
			</>
		),
		image: IMAGES.about.adventures.alaska,
		alt: "Culinary Adventure in Alaska",
		icon: Plane,
		tags: ["Travel", "Alaska", "Thailand", "Nicaragua"],
	},
	{
		year: "Education",
		title: "Teaching the World to Cook",
		description: (
			<>
				<p className="mb-4">
					Knowledge is meant to be shared. From teaching culinary bootcamps in
					50+ gyms worldwide to launching <strong>Ice Age Culinary</strong>, our
					online cooking school.
				</p>
				<p>
					My goal is to empower you in your own kitchen. Whether it&apos;s
					through our delivered meals or teaching you to chop an onion, we are
					partners in your health journey.
				</p>
			</>
		),
		image: IMAGES.about.cookingLessons,
		alt: "Nick teaching a cooking class",
		icon: ChefHat,
		tags: ["Ice Age Culinary", "Education", "Cooking School"],
	},
	{
		year: "Lifestyle",
		title: 'The "Big Life" Philosophy',
		description: (
			<>
				<p className="mb-4">
					<strong>"Fuel The Big Life"</strong> isn&apos;t just a tagline.
					It&apos;s about moments like dock smoothies on Lake Lizzie with
					friends, landing a Muskie, or sharing a meal after a hard workout.
				</p>
				<p>
					We exist to give you time back, so you can chase <em>your</em> big
					life, whatever that looks like.
				</p>
			</>
		),
		image: IMAGES.about.muskie,
		alt: "Nick with a Muskie catch",
		icon: Anchor,
		tags: ["Lifestyle", "Fun", "Balance"],
	},
];

function ParallaxImage({
	src,
	alt,
	className,
	position = "object-center",
	fit = "cover",
}: {
	src: string;
	alt: string;
	className?: string;
	position?: string;
	fit?: "cover" | "contain";
}) {
	const ref = useRef(null);
	const { scrollYProgress } = useScroll({
		target: ref,
		offset: ["start end", "end start"],
	});
	const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
	const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.1, 1, 1.1]);

	return (
		<div
			ref={ref}
			className={`relative overflow-hidden bg-secondary/20 ${className}`}
		>
			<motion.div
				style={{ y, scale }}
				className="absolute inset-0 w-full h-full"
			>
				<Image
					src={src}
					alt={alt}
					fill
					className={`object-${fit} ${position}`}
					sizes="(max-width: 768px) 100vw, 50vw"
				/>
			</motion.div>
		</div>
	);
}

import {
	Carousel,
	CarouselContent,
	CarouselItem,
	CarouselNext,
	CarouselPrevious,
} from "@/components/ui/carousel";

function TimelineCard({ item, index }: { item: TimelineItem; index: number }) {
	const isEven = index % 2 === 0;
	const ref = useRef(null);
	const { scrollYProgress } = useScroll({
		target: ref,
		offset: ["start end", "center center"],
	});

	const opacity = useSpring(useTransform(scrollYProgress, [0, 0.5], [0, 1]), {
		stiffness: 100,
		damping: 20,
	});
	const x = useSpring(
		useTransform(scrollYProgress, [0, 0.5], [isEven ? -50 : 50, 0]),
		{ stiffness: 100, damping: 20 },
	);

	const aspectRatioClass =
		item.aspectRatio || "aspect-[4/3] md:aspect-[3/4] lg:aspect-[4/3]";
	const widthClass =
		item.aspectRatio === "aspect-[3/4]"
			? "w-[70%] max-w-[400px] mx-auto"
			: "w-full";

	return (
		<div
			ref={ref}
			className={`relative flex flex-col md:flex-row items-center gap-8 md:gap-16 py-12 md:py-24 ${isEven ? "" : "md:flex-row-reverse"}`}
		>
			<motion.div
				style={{ opacity, x }}
				className="w-full md:w-1/2 relative group"
			>
				<div className={`relative ${widthClass}`}>
					<div
						className={`${aspectRatioClass} rounded-3xl overflow-hidden shadow-xl dark:shadow-[0_10px_40px_rgba(255,255,255,0.05)] border-4 border-background ring-1 ring-border relative z-10 bg-background transition-transform duration-700 hover:scale-[1.02]`}
					>
						<ParallaxImage
							src={item.image}
							alt={item.alt}
							className="w-full h-full"
							position={item.imageObjectPosition}
							fit={item.fit}
						/>

						<div
							className={`absolute top-6 ${isEven ? "right-6" : "left-6"} bg-background/90 backdrop-blur-md p-3 rounded-xl shadow-lg border border-border/50 z-20`}
						>
							<item.icon className="w-6 h-6 text-primary" />
						</div>
					</div>
					<div
						className={`absolute -bottom-6 -z-10 w-full h-full bg-zinc-200 dark:bg-zinc-800 rounded-3xl ${isEven ? "-right-6" : "-left-6"}`}
					/>
				</div>
			</motion.div>

			<motion.div
				style={{
					opacity,
					x: useSpring(
						useTransform(scrollYProgress, [0, 0.5], [isEven ? 50 : -50, 0]),
					),
				}}
				className="w-full md:w-1/2 space-y-6"
			>
				<div className="flex items-center gap-3">
					<Badge
						variant="outline"
						className="px-3 py-1 text-sm border-primary/20 text-primary bg-primary/10"
					>
						{item.year || "Milestone"}
					</Badge>
					<div className="h-px bg-border flex-1" />
				</div>

				<h2 className="text-3xl md:text-5xl font-bold tracking-tight text-balance leading-tight">
					{item.title}
				</h2>

				<div className="text-lg text-muted-foreground leading-relaxed">
					{item.description}
				</div>

				<div className="flex flex-wrap gap-2 pt-2">
					{item.tags?.map((tag) => (
						<span
							key={tag}
							className="text-xs font-semibold uppercase tracking-wider text-secondary-foreground/80 bg-secondary px-2 py-1 rounded"
						>
							#{tag}
						</span>
					))}
				</div>
			</motion.div>
		</div>
	);
}

function SharkTankSection() {
	const images = [
		{
			src: IMAGES.about.sharkTank.serving,
			alt: "Serving the Sharks",
		},
		{
			src: IMAGES.about.sharkTank.markCuban,
			alt: "Mark Cuban",
		},
		{
			src: IMAGES.about.sharkTank.set,
			alt: "Shark Tank Set",
		},
		{
			src: IMAGES.about.sharkTank.serving,
			alt: "Serving",
		},
	];

	return (
		<section className="relative py-24 bg-black text-white overflow-hidden my-12 md:my-24">
			<div className="absolute inset-0 opacity-30">
				<Image
					src={IMAGES.about.sharkTank.set}
					alt="Shark Tank Set"
					fill
					className="object-cover"
					sizes="100vw"
				/>
				<div className="absolute inset-0 bg-linear-to-r from-black via-black/90 to-black/40" />
			</div>

			<div className="container mx-auto px-4 relative z-10">
				<div className="grid md:grid-cols-2 gap-12 items-center">
					<div className="space-y-8">
						<div className="inline-flex items-center gap-2 text-yellow-500 font-bold tracking-wider uppercase">
							<Tv className="w-5 h-5" /> As seen on TV
						</div>
						<h2 className="text-4xl md:text-6xl font-black tracking-tighter">
							THE SHARK TANK{" "}
							<span className="text-transparent bg-clip-text bg-linear-to-r from-blue-400 to-cyan-400">
								EFFECT
							</span>
						</h2>
						<p className="text-xl text-zinc-300 leading-relaxed max-w-xl">
							Pitching our Butternut Squash Lasagna to the sharks wasn&apos;t
							just business—it was about validating a movement. We stood our
							ground, proved our quality, and showed the world that frozen food
							can be <strong>elite</strong>.
						</p>
					</div>

					<div className="w-full">
						<Carousel
							opts={{
								align: "start",
								loop: true,
							}}
							className="w-full max-w-lg mx-auto md:mr-0"
						>
							<CarouselContent>
								{images.map((img, idx) => (
									// biome-ignore lint/suspicious/noArrayIndexKey: Images are static
									<CarouselItem key={idx} className="basis-full pl-4">
										<div className="rounded-xl overflow-hidden aspect-video border border-zinc-800 shadow-2xl relative group bg-zinc-900">
											<Image
												src={img.src}
												alt={img.alt}
												fill
												className="object-cover group-hover:scale-105 transition-transform duration-500"
												sizes="(max-width: 768px) 100vw, 50vw"
											/>
										</div>
									</CarouselItem>
								))}
							</CarouselContent>
							<CarouselPrevious className="left-2 bg-black/50 border-white/20 hover:bg-black" />
							<CarouselNext className="right-2 bg-black/50 border-white/20 hover:bg-black" />
						</Carousel>
					</div>
				</div>
			</div>
		</section>
	);
}

export function AboutContent() {
	return (
		<div className="bg-background min-h-screen selection:bg-primary/30 overflow-x-hidden">
			<section className="relative h-[80vh] flex items-center justify-center overflow-hidden">
				<div className="absolute inset-0 z-0">
					<Image
						src={IMAGES.about.adventures.dockSmoothies}
						alt="Ice Age Meals Lifestyle"
						fill
						className="object-cover brightness-[0.6]"
						priority
						sizes="100vw"
					/>
					<div className="absolute inset-0 bg-linear-to-b from-transparent via-background/20 to-background" />
				</div>

				<div className="container relative z-10 text-center text-white space-y-8 px-4">
					<motion.div
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8, ease: "easeOut" }}
					>
						<Badge className="bg-white/10 text-white hover:bg-white/20 backdrop-blur-md mb-6 px-4 py-2 text-sm border-white/20">
							EST. 2015
						</Badge>
						<h1 className="text-5xl md:text-8xl font-black tracking-tighter mb-6 text-balance drop-shadow-2xl">
							OUR STORY<span className="text-primary">.</span>
						</h1>
						<p className="text-xl md:text-2xl text-zinc-200 max-w-2xl mx-auto font-light leading-relaxed">
							From a CrossFit food booth to Shark Tank and beyond. This is how
							we are changing the way the world eats.
						</p>
					</motion.div>
				</div>

				<motion.div
					className="absolute bottom-10 left-1/2 -translate-x-1/2 text-foreground/80"
					animate={{ y: [0, 10, 0] }}
					transition={{ repeat: Infinity, duration: 2 }}
				>
					<div className="flex flex-col items-center gap-2 text-xs uppercase tracking-widest">
						<span>Scroll to explore</span>
						<div className="w-px h-12 bg-linear-to-b from-foreground/0 via-foreground/50 to-foreground/0" />
					</div>
				</motion.div>
			</section>

			<div className="container mx-auto px-4 max-w-6xl">
				<TimelineCard item={timelineData[0]} index={0} />
				<TimelineCard item={timelineData[1]} index={1} />
			</div>

			<SharkTankSection />

			<div className="container mx-auto px-4 max-w-6xl pb-24">
				<TimelineCard item={timelineData[3]} index={2} />
				<TimelineCard item={timelineData[4]} index={3} />
				<TimelineCard item={timelineData[5]} index={4} />
			</div>

			<section className="bg-black text-white py-24 border-t border-white/10">
				<div className="container mx-auto px-4">
					<div className="text-center max-w-2xl mx-auto mb-16">
						<h2 className="text-3xl md:text-5xl font-black mb-4 tracking-tighter">
							ADVENTURES IN THE WILD
						</h2>
						<p className="text-zinc-400 text-lg">
							Snapshot moments from our Culinary Adventures around the world.
						</p>
					</div>

					<motion.div
						className="grid grid-cols-2 md:grid-cols-4 gap-4 h-150 md:h-150"
						initial="hidden"
						whileInView="visible"
						viewport={{ once: true }}
						variants={{
							visible: { transition: { staggerChildren: 0.1 } },
						}}
					>
						<motion.div
							variants={{
								hidden: { opacity: 0, scale: 0.9 },
								visible: { opacity: 1, scale: 1 },
							}}
							className="col-span-2 row-span-2 relative rounded-3xl overflow-hidden group border border-white/10"
						>
							<Image
								src={IMAGES.about.adventures.nicaragua}
								alt="Nicaragua"
								fill
								className="object-cover group-hover:scale-110 transition-transform duration-700"
								sizes="(max-width: 768px) 100vw, 50vw"
							/>
							<div className="absolute inset-0 bg-linear-to-t from-black/80 via-transparent to-transparent opacity-60 group-hover:opacity-80 transition-opacity" />
							<div className="absolute bottom-0 left-0 right-0 p-8 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
								<div className="font-bold text-2xl mb-1">Nicaragua</div>
								<div className="text-sm text-zinc-300">Beach Workouts</div>
							</div>
						</motion.div>

						<motion.div
							variants={{
								hidden: { opacity: 0, scale: 0.9 },
								visible: { opacity: 1, scale: 1 },
							}}
							className="col-span-1 row-span-1 relative rounded-3xl overflow-hidden group border border-white/10"
						>
							<Image
								src={IMAGES.about.adventures.thailand}
								alt="Thailand"
								fill
								className="object-cover group-hover:scale-110 transition-transform duration-700"
								sizes="(max-width: 768px) 50vw, 25vw"
							/>
						</motion.div>

						<motion.div
							variants={{
								hidden: { opacity: 0, scale: 0.9 },
								visible: { opacity: 1, scale: 1 },
							}}
							className="col-span-1 row-span-2 relative rounded-3xl overflow-hidden group border border-white/10"
						>
							<Image
								src={IMAGES.about.adventures.dockDinner}
								alt="Dock Dinner"
								fill
								className="object-cover group-hover:scale-110 transition-transform duration-700"
								sizes="(max-width: 768px) 50vw, 25vw"
							/>
							<div className="absolute inset-0 bg-linear-to-t from-black/80 via-transparent to-transparent opacity-60 group-hover:opacity-80 transition-opacity" />
							<div className="absolute bottom-0 left-0 right-0 p-8 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
								<div className="font-bold text-xl mb-1">Lake Lizzie</div>
								<div className="text-sm text-zinc-300">Dock Dinners</div>
							</div>
						</motion.div>

						<motion.div
							variants={{
								hidden: { opacity: 0, scale: 0.9 },
								visible: { opacity: 1, scale: 1 },
							}}
							className="col-span-1 row-span-1 relative rounded-3xl overflow-hidden group border border-white/10"
						>
							<Image
								src={IMAGES.about.adventures.dockSmoothies}
								alt="Smoothies"
								fill
								className="object-cover group-hover:scale-110 transition-transform duration-700"
								sizes="(max-width: 768px) 50vw, 25vw"
							/>
						</motion.div>
					</motion.div>
				</div>
			</section>

			<section className="py-32 container mx-auto px-4 text-center">
				<motion.div
					initial={{ opacity: 0, scale: 0.9 }}
					whileInView={{ opacity: 1, scale: 1 }}
					viewport={{ once: true }}
					className="max-w-2xl mx-auto space-y-8"
				>
					<div className="inline-block p-4 rounded-full bg-primary/10 mb-4">
						<ChefHat className="w-8 h-8 text-primary" />
					</div>
					<h2 className="text-4xl md:text-6xl font-black text-balance">
						Ready to fuel <br />
						<span className="text-primary">your</span> big life?
					</h2>
					<p className="text-xl text-muted-foreground">
						Join thousands of others who have taken back their time and health
						with Ice Age Meals.
					</p>
					<div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
						<Button
							size="lg"
							className="h-14 px-8 text-lg rounded-full shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-shadow"
							asChild
						>
							<Link href="/collections/ice-age-meals-2024">
								Build Your Box <ArrowRight className="ml-2 w-5 h-5" />
							</Link>
						</Button>
						<Button
							variant="outline"
							size="lg"
							className="h-14 px-8 text-lg rounded-full"
							asChild
						>
							<Link href="/contact">Contact Us</Link>
						</Button>
					</div>
				</motion.div>
			</section>
		</div>
	);
}
